import React, { useState } from 'react'
import {
  Bold, Italic, Underline, Strikethrough, Type, Palette,
  Highlighter, RemoveFormatting, ChevronDown,
} from 'lucide-react'
import { cn } from '../../utils/cn'

// Inline-formatting ribbon. Appears above a text selection inside a note block.
// All buttons use onMouseDown+preventDefault so the text selection is never
// lost when the toolbar is clicked.

const TEXT_COLORS = [
  { label: 'Default', value: '#151B25' },
  { label: 'Red',     value: '#CE3A33' },
  { label: 'Amber',   value: '#B45309' },
  { label: 'Green',   value: '#099250' },
  { label: 'Blue',    value: '#214AD4' },
  { label: 'Violet',  value: '#7C3AED' },
  { label: 'Slate',   value: '#677488' },
]
const HIGHLIGHTS = [
  { label: 'None',   value: 'transparent' },
  { label: 'Yellow', value: '#FEF3C7' },
  { label: 'Green',  value: '#D1FADF' },
  { label: 'Blue',   value: '#DCE7FF' },
  { label: 'Red',    value: '#FEE4E2' },
  { label: 'Violet', value: '#EDE9FE' },
]
const FONTS = [
  { label: 'Sans',  value: "Inter, ui-sans-serif, system-ui, sans-serif" },
  { label: 'Serif', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Mono',  value: "ui-monospace, 'SF Mono', Menlo, monospace" },
]
const SIZES = [
  { label: 'Small',  value: '0.85rem' },
  { label: 'Normal', value: '1rem' },
  { label: 'Large',  value: '1.35rem' },
  { label: 'Huge',   value: '1.8rem' },
]

function IconBtn({ active, onApply, title, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={!!active}
      onMouseDown={(e) => { e.preventDefault(); onApply() }}
      className={cn(
        'flex items-center justify-center w-8 h-8 rounded-lg transition-colors',
        active ? 'bg-accent-500 text-white' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
      )}
    >
      {children}
    </button>
  )
}

function Dropdown({ icon: Icon, label, open, setOpen, children }) {
  return (
    <div className="relative">
      <button
        type="button"
        title={label}
        onMouseDown={(e) => { e.preventDefault(); setOpen(open ? null : label) }}
        className="flex items-center gap-0.5 h-8 px-1.5 rounded-lg text-ink-600 hover:bg-ink-100 hover:text-ink-900 transition-colors"
      >
        <Icon className="w-4 h-4" />
        <ChevronDown className="w-3 h-3" />
      </button>
      {open === label && (
        <div className="absolute top-full left-0 mt-1 z-10 rounded-xl bg-white border border-ink-100 shadow-elev-3 py-1 animate-scale-in min-w-[9rem]">
          {children}
        </div>
      )}
    </div>
  )
}

export default function SelectionToolbar({ pos, active, onCommand }) {
  const [open, setOpen] = useState(null)   // which dropdown is open (by label)

  if (!pos) return null

  // Position above the selection, clamped to the editor's left edge.
  const left = Math.max(8, pos.left)
  const style = { top: Math.max(0, pos.top - 50), left, transform: 'translateX(-50%)' }

  const fire = (type, value) => { onCommand(type, value); setOpen(null) }

  return (
    <div
      className="absolute z-40 animate-scale-in"
      style={style}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-0.5 rounded-xl bg-white border border-ink-150 shadow-elev-3 px-1 py-1">
        <IconBtn title="Bold (⌘B)"          active={active.bold}          onApply={() => fire('exec', 'bold')}><Bold className="w-4 h-4" /></IconBtn>
        <IconBtn title="Italic (⌘I)"        active={active.italic}        onApply={() => fire('exec', 'italic')}><Italic className="w-4 h-4" /></IconBtn>
        <IconBtn title="Underline (⌘U)"     active={active.underline}     onApply={() => fire('exec', 'underline')}><Underline className="w-4 h-4" /></IconBtn>
        <IconBtn title="Strikethrough"      active={active.strikeThrough} onApply={() => fire('exec', 'strikeThrough')}><Strikethrough className="w-4 h-4" /></IconBtn>

        <span className="w-px h-5 bg-ink-150 mx-0.5" />

        {/* Font size */}
        <Dropdown icon={Type} label="Size" open={open} setOpen={setOpen}>
          {SIZES.map((s) => (
            <button key={s.label} type="button"
              onMouseDown={(e) => { e.preventDefault(); fire('size', s.value) }}
              className="w-full flex items-center px-3 py-1.5 text-sm text-ink-700 hover:bg-ink-50 text-left">
              <span style={{ fontSize: s.value === '1.8rem' ? '1.2rem' : s.value }}>{s.label}</span>
            </button>
          ))}
        </Dropdown>

        {/* Font family */}
        <Dropdown icon={() => <span className="text-[13px] font-semibold leading-none">F</span>} label="Font" open={open} setOpen={setOpen}>
          {FONTS.map((f) => (
            <button key={f.label} type="button"
              onMouseDown={(e) => { e.preventDefault(); fire('font', f.value) }}
              className="w-full px-3 py-1.5 text-sm text-ink-700 hover:bg-ink-50 text-left" style={{ fontFamily: f.value }}>
              {f.label}
            </button>
          ))}
        </Dropdown>

        {/* Text color */}
        <Dropdown icon={Palette} label="Color" open={open} setOpen={setOpen}>
          <div className="px-2 py-1 grid grid-cols-4 gap-1.5">
            {TEXT_COLORS.map((c) => (
              <button key={c.label} type="button" title={c.label}
                onMouseDown={(e) => { e.preventDefault(); fire('color', c.value) }}
                className="w-6 h-6 rounded-md ring-1 ring-ink-200 hover:scale-110 transition-transform flex items-center justify-center"
                style={{ color: c.value }}>
                <span className="text-[13px] font-bold leading-none">A</span>
              </button>
            ))}
          </div>
        </Dropdown>

        {/* Highlight */}
        <Dropdown icon={Highlighter} label="Highlight" open={open} setOpen={setOpen}>
          <div className="px-2 py-1 grid grid-cols-3 gap-1.5">
            {HIGHLIGHTS.map((c) => (
              <button key={c.label} type="button" title={c.label}
                onMouseDown={(e) => { e.preventDefault(); fire('highlight', c.value) }}
                className="w-7 h-7 rounded-md ring-1 ring-ink-200 hover:scale-110 transition-transform"
                style={{ backgroundColor: c.value === 'transparent' ? '#fff' : c.value }}>
                {c.value === 'transparent' && <span className="text-[10px] text-ink-400">⌀</span>}
              </button>
            ))}
          </div>
        </Dropdown>

        <span className="w-px h-5 bg-ink-150 mx-0.5" />

        <IconBtn title="Clear formatting" onApply={() => fire('clear')}>
          <RemoveFormatting className="w-4 h-4" />
        </IconBtn>
      </div>
    </div>
  )
}
